import { observer } from 'mobx-react-lite';
import React, { Fragment, useContext, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import {
  Button,
  Checkbox,
  Header,
  Icon,
  Label,
  Modal,
  Reveal,
  Table,
} from 'semantic-ui-react';

import {
  formatDetailsDisp,
  PROCESS_STATE,
  TICKET_RESERVE_TYPE,
} from '../../../app/common/util/util';
import { IQrTicket } from '../../../app/models/qrticket';
import { RootStoreContext } from '../../../app/stores/rootStore';

interface IProps {
  salesId: string;
}

const QrTicketList: React.FC<IProps> = ({ salesId }) => {
  const rootStore = useContext(RootStoreContext);
  const { depositSalesTicket } = rootStore.depositSalesTicketStore;
  const {
    submitting,
    qrTicketsCount,
    qrTickets,
    activeQrTickets,
    csvData,
    partiallyDlQrTickets,
    partiallyPrintQrTickets,
    issueQrTickets,
    enter,
    exit,
    deleteQrTickets,
  } = rootStore.qrTicketStore;

  const csvInstance = useRef<any | null>(null);
  const [qrticketIds, setQrticketIds] = useState<number[]>([]);
  const [dlModalOpen, setDlModalOpen] = useState(false);
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const handleChangeHeaderRowCheckbox = (data: any) => {
    data.checked
      ? setQrticketIds(activeQrTickets.map((row) => row.qrticketId))
      : setQrticketIds([]);
  };

  const handleChangeRowCheckbox = (qrTicket: IQrTicket) => {
    const newRow = qrTicket.qrticketId;
    qrticketIds.includes(newRow)
      ? setQrticketIds((old) => old.filter((row) => row !== newRow))
      : setQrticketIds((old) => [...old, newRow]);
  };

  const handleCsvDownload = () => {
    issueQrTickets(salesId, qrticketIds).then(() => {
      if (csvInstance && csvInstance.current && csvInstance.current.link) {
        partiallyDlQrTickets(salesId, qrticketIds).then(() => {
          csvInstance.current.link.click();
          setDlModalOpen(false);
        });
      }
    });
  };

  const handlePrintTicket = () => {
    issueQrTickets(salesId, qrticketIds).then(() => {
      partiallyPrintQrTickets(salesId, qrticketIds).then(() => {
        setPrintModalOpen(false);
      });
    });
  };

  const handleDeleteTicket = () => {
    deleteQrTickets(qrticketIds).then(() => {
      setQrticketIds([]);
      setDeleteModalOpen(false);
    });
  };

  return (
    <Fragment>
      <Label size='large'>
        検索結果 : {qrTicketsCount.toLocaleString()} 件
      </Label>
      <Modal
        trigger={
          <Button
            icon
            labelPosition='left'
            color='blue'
            disabled={
              depositSalesTicket &&
              depositSalesTicket.ticketReserveTypeEnum ===
                TICKET_RESERVE_TYPE.Api &&
              qrticketIds.length > 0
                ? false
                : true
            }
            onClick={() => {
              setDlModalOpen(true);
            }}
          >
            <Icon name='download' />
            DL
          </Button>
        }
        open={dlModalOpen}
        onClose={() => setDlModalOpen(false)}
        basic
        size='small'
      >
        <Header icon='download' />
        <Modal.Content>
          <h3>QR送信して、よろしいでしょうか？</h3>
        </Modal.Content>
        <Modal.Actions>
          <CSVLink data={csvData} filename='data.csv' ref={csvInstance} />
          <Button
            basic
            color='green'
            inverted
            floated='right'
            loading={submitting}
            onClick={() => handleCsvDownload()}
          >
            <Icon name='checkmark' />
            はい
          </Button>
          <Button color='red' inverted onClick={() => setDlModalOpen(false)}>
            <Icon name='remove' />
            いいえ
          </Button>
        </Modal.Actions>
      </Modal>
      <Modal
        trigger={
          <Button
            icon
            labelPosition='left'
            color='blue'
            disabled={
              depositSalesTicket &&
              depositSalesTicket.ticketReserveTypeEnum ===
                TICKET_RESERVE_TYPE.Normal &&
              qrticketIds.length > 0
                ? false
                : true
            }
            onClick={() => {
              setPrintModalOpen(true);
            }}
          >
            <Icon name='print' />
            発券
          </Button>
        }
        open={printModalOpen}
        onClose={() => setPrintModalOpen(false)}
        basic
        size='small'
      >
        <Header icon='print' />
        <Modal.Content>
          <h3>発券して、よろしいでしょうか？</h3>
        </Modal.Content>
        <Modal.Actions>
          <Button
            basic
            color='green'
            inverted
            floated='right'
            loading={submitting}
            onClick={() => handlePrintTicket()}
          >
            <Icon name='checkmark' />
            はい
          </Button>
          <Button color='red' inverted onClick={() => setPrintModalOpen(false)}>
            <Icon name='remove' />
            いいえ
          </Button>
        </Modal.Actions>
      </Modal>
      <Modal
        trigger={
          <Button
            floated='right'
            icon
            labelPosition='left'
            color='red'
            disabled={qrticketIds.length > 0 ? false : true}
            onClick={() => {
              setDeleteModalOpen(true);
            }}
          >
            <Icon name='trash' />
            廃券
          </Button>
        }
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        basic
        size='small'
      >
        <Header icon='trash' />
        <Modal.Content>
          <h3>廃券にして、よろしいでしょうか？</h3>
        </Modal.Content>
        <Modal.Actions>
          <Button
            basic
            color='green'
            inverted
            floated='right'
            loading={submitting}
            onClick={() => handleDeleteTicket()}
          >
            <Icon name='checkmark' />
            はい
          </Button>
          <Button
            color='red'
            inverted
            onClick={() => setDeleteModalOpen(false)}
          >
            <Icon name='remove' />
            いいえ
          </Button>
        </Modal.Actions>
      </Modal>

      <Table celled compact striped selectable definition size='small'>
        <Table.Header fullWidth>
          <Table.Row>
            <Table.HeaderCell width='1' textAlign='center'>
              <Checkbox
                indeterminate={
                  qrticketIds.length > 0 &&
                  qrticketIds.length < activeQrTickets.length
                }
                checked={
                  activeQrTickets.length > 0 &&
                  qrticketIds.length === activeQrTickets.length
                }
                onChange={(_e, data) => handleChangeHeaderRowCheckbox(data)}
              />
            </Table.HeaderCell>
            <Table.HeaderCell width='1'>ステータス</Table.HeaderCell>
            <Table.HeaderCell>QRコード</Table.HeaderCell>
            <Table.HeaderCell width='3'>発券日</Table.HeaderCell>
            <Table.HeaderCell width='3'>入場日</Table.HeaderCell>
            <Table.HeaderCell width='3'>入場ステータス</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {qrTickets.map((qrTicket: IQrTicket) => (
            <Table.Row
              key={qrTicket.qrticketId}
              negative={qrTicket.deleteFlagEnum === PROCESS_STATE.Done}
            >
              <Table.Cell textAlign='center'>
                <Checkbox
                  checked={qrticketIds.includes(qrTicket.qrticketId)}
                  onChange={() => handleChangeRowCheckbox(qrTicket)}
                  disabled={qrTicket.deleteFlagEnum === PROCESS_STATE.Done}
                />
              </Table.Cell>
              <Table.Cell>
                {qrTicket.deleteFlagEnum === PROCESS_STATE.Done ? (
                  <Label color='red' ribbon>
                    廃券
                  </Label>
                ) : qrTicket.ticketReserveStatusEnum === PROCESS_STATE.Done ? (
                  <Label color='teal' ribbon>
                    発券済
                  </Label>
                ) : (
                  ''
                )}
              </Table.Cell>
              <Table.Cell>{qrTicket.qrcode}</Table.Cell>
              <Table.Cell>
                {qrTicket.ticketReserveTime &&
                  formatDetailsDisp(new Date(qrTicket.ticketReserveTime))}
              </Table.Cell>
              <Table.Cell>
                {qrTicket.startTime1 &&
                  formatDetailsDisp(new Date(qrTicket.startTime1))}
              </Table.Cell>
              <Table.Cell singleLine>
                <Reveal
                  animated='move'
                  disabled={qrTicket.deleteFlagEnum === PROCESS_STATE.Done}
                >
                  <Reveal.Content visible>
                    <Button
                      style={{ width: '240px' }}
                      compact
                      color={
                        qrTicket.ticketEnterStatusEnum === PROCESS_STATE.Done
                          ? 'green'
                          : 'olive'
                      }
                      content={
                        qrTicket.ticketEnterStatusEnum === PROCESS_STATE.Done
                          ? '入場済'
                          : '未入場'
                      }
                    />
                  </Reveal.Content>
                  <Reveal.Content hidden>
                    <Button
                      style={{ width: '240px' }}
                      loading={submitting}
                      compact
                      basic
                      color={
                        qrTicket.ticketEnterStatusEnum === PROCESS_STATE.Done
                          ? 'olive'
                          : 'green'
                      }
                      content={
                        qrTicket.ticketEnterStatusEnum === PROCESS_STATE.Done
                          ? '取消'
                          : '入場'
                      }
                      onClick={
                        qrTicket.ticketEnterStatusEnum === PROCESS_STATE.Done
                          ? () => exit(qrTicket.qrticketId)
                          : () => enter(qrTicket.qrticketId)
                      }
                    />
                  </Reveal.Content>
                </Reveal>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </Fragment>
  );
};

export default observer(QrTicketList);
